import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { TrendUp, Gavel, Target } from "@phosphor-icons/react";
import CityCombobox from "@/components/CityCombobox";

export default function Opportunita() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("milano");
  const [data, setData] = useState({ top_yield_cells: [], top_auctions: [] });

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);
  useEffect(() => {
    api.get(`/opportunities?city=${city}&limit=12`).then((r) => setData(r.data));
  }, [city]);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— MOTORE OPPORTUNITÀ</div>
            <h1 className="font-display font-black text-3xl">Dove investire adesso.</h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Aree con il miglior rapporto <span className="text-zinc-100 font-mono-data">€/m² × rendimento</span>, incrociate con aste in vendita nelle stesse zone.</p>
          </div>
          <div className="min-w-[220px]">
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
            <div className="mt-1">
              <CityCombobox value={city} onChange={setCity} cities={cities} testId="opp-city-select" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid lg:grid-cols-2 gap-6">
        {/* Top yield */}
        <div className="border border-white/10">
          <div className="flex items-center gap-2 p-4 border-b border-white/10 bg-zinc-900/50">
            <TrendUp size={16} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">TOP AREE PER RENDIMENTO</span>
          </div>
          <div className="divide-y divide-white/5" data-testid="top-yield-list">
            {data.top_yield_cells.map((c, idx) => (
              <div key={`${c.i}-${c.j}`} className="p-4 hover:bg-white/[0.02] transition-colors" data-testid={`yield-cell-${c.i}-${c.j}`}>
                <div className="flex items-center gap-3">
                  <div className="font-mono-data text-2xl font-black text-zinc-600 w-8">{String(idx + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <div className="font-mono-data text-sm text-zinc-100 mb-0.5">Cella #{c.i}-{c.j}</div>
                    <div className="font-mono-data text-[11px] text-zinc-500">{c.n_listings} immobili · {formatEuro(c.avg_price_sqm)}/m²</div>
                  </div>
                  <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-xs font-bold px-3 py-1 hover:bg-lime-400">
                    {c.yield_pct}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top auctions */}
        <div className="border border-white/10">
          <div className="flex items-center gap-2 p-4 border-b border-white/10 bg-zinc-900/50">
            <Gavel size={16} weight="bold" className="text-amber-400" />
            <span className="font-mono-data text-xs text-amber-400 tracking-widest">TOP ASTE PER SCONTO</span>
          </div>
          <div className="divide-y divide-white/5" data-testid="top-auctions-list">
            {data.top_auctions.map((a, idx) => (
              <div key={a.id} className="p-4 hover:bg-white/[0.02] transition-colors" data-testid={`opp-auction-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className="font-mono-data text-2xl font-black text-zinc-600 w-8">{String(idx + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <div className="font-mono-data text-sm text-zinc-100 mb-0.5">{a.type} · {a.zone}</div>
                    <div className="font-mono-data text-[11px] text-zinc-500">
                      <span className="text-amber-400 font-bold">{formatEuro(a.base_price)}</span>
                      <span className="text-zinc-600 mx-1">/</span>
                      <span className="line-through">{formatEuro(a.market_price)}</span>
                      <span className="mx-1">·</span>{a.auction_date}
                    </div>
                  </div>
                  <Badge className="rounded-none bg-amber-400 text-amber-950 font-mono-data text-xs font-bold px-3 py-1 hover:bg-amber-400">
                    -{a.discount_pct}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 border border-lime-400/30 bg-lime-400/[0.03] p-5 flex gap-4 items-start">
          <Target size={22} weight="bold" className="text-lime-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-lg mb-1">Strategia consigliata</div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Cerca aste che ricadono nelle prime 3 celle per rendimento: acquistando sotto valore in aree con canoni sostenuti,
              massimizzi il ROI e riduci il tempo di rientro dell'investimento. Verifica sempre lo stato dell'immobile e la
              perizia depositata in tribunale.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
